import {UserRepository} from '../repository/UserRepository';
import {DashboardUser} from '../models/user.model';
import {AppError, ErrorType} from '../../../Data/config/AppError';

export class DeactivateUserUseCase {
  private readonly userRepository: UserRepository;

  constructor (userRepository: UserRepository) {
    this.userRepository = userRepository;
  }

  async execute (userId: number, currentUserId?: number): Promise<DashboardUser> {
    // Validate user ID
    if (!userId || userId < 1) {
      throw new AppError(
        'Valid user ID is required',
        ErrorType.VALIDATION,
        {value: userId, field: 'userId'}
      );
    }

    // Prevent self-deactivation (optional business rule)
    if (currentUserId && userId === currentUserId) {
      throw new AppError(
        'Cannot deactivate your own account',
        ErrorType.VALIDATION,
        {value: {userId, currentUserId}}
      );
    }

    try {
      // Deactivate the user
      const deactivatedUser = await this.userRepository.deactivateUser(userId);

      return deactivatedUser;
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }

      // Handle specific API errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('user not found')) {
        throw new AppError(
          'User not found',
          ErrorType.NOT_FOUND,
          {value: userId, field: 'userId'}
        );
      }

      if (errorMessage.includes('user already inactive')) {
        throw new AppError(
          'User is already inactive',
          ErrorType.VALIDATION,
          {value: userId, field: 'userId'}
        );
      }

      if (errorMessage.includes('cannot deactivate admin user')) {
        throw new AppError(
          'Cannot deactivate admin user',
          ErrorType.VALIDATION,
          {value: userId, field: 'userId'}
        );
      }

      if (errorMessage.includes('insufficient permissions')) {
        throw new AppError(
          'Insufficient permissions to deactivate user',
          ErrorType.AUTHORIZATION,
          {value: userId, field: 'userId'}
        );
      }

      throw new AppError(
        'Failed to deactivate user',
        ErrorType.API_ERROR,
        undefined,
        undefined,
        error
      );
    }
  }
}
